import React from 'react';
import {
  Target,
  ChartBar,
  ArrowRight,
  Users,
  Building2,
  GitBranch,
  Globe,
  AlertTriangle,
  CheckCircle,
  Workflow,
  Database,
  Network,
  Shield
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && <div className="text-sm text-gray-500 mt-1">{trend}</div>}
  </div>
);

const KenyaDigitalFinance = () => {
  const projectContext = {
    vision: "Modernisation de l'infrastructure financière nationale du Kenya",
    overview: {
      phase: "Phase 1 - Infrastructure",
      timeline: "2024-2026",
      budget: "75M$",
      status: "Démarrage"
    },
    objectives: [
      {
        title: "Infrastructure",
        items: [
          "Modernisation KEPSS (Kenya Electronic Payment and Settlement System)",
          "Infrastructure Open Banking EAC",
          "Switch national unifié",
          "Hub d'interopérabilité régional"
        ]
      },
      {
        title: "Services",
        items: [
          "APIs standardisées",
          "Paiements instantanés",
          "Settlement temps réel",
          "Services transfrontaliers EAC"
        ]
      },
      {
        title: "Réglementation",
        items: [
          "Framework Open Banking",
          "Standards EAC",
          "Supervision digitale",
          "Contrôles AML/KYC"
        ]
      }
    ]
  };

  const technicalComponents = {
    core: [
      {
        name: "KEPSS Next Gen",
        description: "Système de règlement modernisé",
        features: [
          "Settlement temps réel",
          "APIs ISO 20022",
          "Monitoring 24/7",
          "Reporting automatisé"
        ],
        technologies: [
          "Microservices",
          "Event Streaming",
          "APIs RESTful",
          "Oracle DB"
        ],
        status: "En développement"
      },
      {
        name: "Open Banking Hub",
        description: "Plateforme Open Banking EAC",
        features: [
          "APIs standardisées",
          "Sandbox développeurs",
          "Analytics temps réel",
          "Gestion consentement"
        ],
        technologies: [
          "API Gateway",
          "OAuth 2.0",
          "Kafka",
          "MongoDB"
        ],
        status: "Planification"
      }
    ],
    security: {
      components: [
        "PKI nationale",
        "HSM central",
        "Système IAM",
        "Audit trail blockchain"
      ],
      standards: [
        "ISO 27001",
        "PCI DSS",
        "ISO 20022",
        "EAC Standards"
      ]
    }
  };

  const implementationPlan = {
    phases: [
      {
        name: "Phase 1 - Infrastructure",
        period: "2024",
        status: "En cours",
        deliverables: [
          "KEPSS modernisé",
          "PKI nationale",
          "Framework sécurité",
          "APIs core"
        ]
      },
      {
        name: "Phase 2 - Open Banking",
        period: "2025",
        status: "Planifié",
        deliverables: [
          "Plateforme Open Banking",
          "Sandbox EAC",
          "Support régional",
          "Analytics"
        ]
      },
      {
        name: "Phase 3 - Innovation",
        period: "2026",
        status: "Prévu",
        deliverables: [
          "Services avancés",
          "Hub innovation",
          "SupTech/RegTech",
          "APIs premium"
        ]
      }
    ],
    governance: {
      committees: [
        "Comité directeur CBK",
        "Groupe technique EAC",
        "Forum banques",
        "Comité régulateurs"
      ],
      metrics: [
        "Disponibilité système",
        "Adoption Open Banking",
        "Conformité EAC",
        "Performance KEPSS"
      ]
    }
  };

  const impacts = {
    market: [
      {
        metric: "Volume transactions",
        target: "x3",
        timeline: "2026"
      },
      {
        metric: "Coûts opérationnels",
        target: "-45%",
        timeline: "2026"
      },
      {
        metric: "Adoption digitale",
        target: "85%",
        timeline: "2026"
      }
    ],
    stakeholders: [
      {
        type: "Banques",
        benefits: [
          "Efficacité opérationnelle",
          "Nouveaux services",
          "Réduction coûts",
          "Intégration EAC"
        ]
      },
      {
        type: "Fintechs",
        benefits: [
          "Accès marché EAC",
          "Innovation facilitée",
          "Time-to-market",
          "Support technique"
        ]
      },
      {
        type: "Régulateurs",
        benefits: [
          "Supervision temps réel",
          "Contrôle risques",
          "Standards harmonisés", 
          "Vision consolidée"
        ]
      }
    ]
  };

  const challenges = [
    {
      category: "Technique",
      items: [
        {
          challenge: "Migration KEPSS",
          impact: "Élevé",
          mitigation: "Approche progressive"
        },
        {
          challenge: "Interopérabilité EAC",
          impact: "Élevé",
          mitigation: "Standards communs"
        }
      ]
    },
    {
      category: "Opérationnel", 
      items: [
        {
          challenge: "Adoption marché",
          impact: "Moyen",
          mitigation: "Programme accompagnement"
        },
        {
          challenge: "Harmonisation EAC",
          impact: "Élevé",
          mitigation: "Groupe travail régional"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Kenya Digital Financial Transformation Program
          </h1>
          <div className="flex flex-wrap gap-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: {projectContext.overview.phase}
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Timeline: {projectContext.overview.timeline}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              Status: {projectContext.overview.status}
            </span>
          </div>
        </div>

        {/* Vision et Objectifs */}
        <Section title="Vision et Objectifs" icon={Target}>
          <div className="space-y-6">
            <AnalysisPoint title="Vision Stratégique" status="info">
              <p className="text-gray-700">{projectContext.vision}</p>
            </AnalysisPoint>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projectContext.objectives.map((obj, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{obj.title}</h3>
                  <ul className="space-y-2">
                    {obj.items.map((item, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Architecture Technique */}
        <Section title="Architecture Technique" icon={GitBranch}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalComponents.core.map((component, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{component.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      component.status === 'En développement'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {component.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{component.description}</p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Fonctionnalités:</h4>
                      <ul className="space-y-1">
                        {component.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-2">Technologies:</h4>
                      <div className="flex flex-wrap gap-2">
                        {component.technologies.map((tech, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-3">Sécurité & Standards</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Composants:</h4>
                  <ul className="space-y-1">
                    {technicalComponents.security.components.map((comp, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Shield className="w-4 h-4 mr-2 text-blue-500" />
                        {comp}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Standards:</h4>
                  <ul className="space-y-1">
                    {technicalComponents.security.standards.map((std, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {std}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Plan Implementation */}
        <Section title="Plan d'Implémentation" icon={Workflow}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {implementationPlan.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.status === 'En cours'
                        ? 'bg-blue-100 text-blue-700'
                        : phase.status === 'Planifié'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Période: {phase.period}</p>
                  <ul className="space-y-1">
                    {phase.deliverables.map((deliverable, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                        {deliverable}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-4">Gouvernance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-2">Comités</h4>
                  <ul className="space-y-1">
                    {implementationPlan.governance.committees.map((committee, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                        {committee}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Métriques</h4>
                  <ul className="space-y-1">
                    {implementationPlan.governance.metrics.map((metric, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <ChartBar className="w-4 h-4 mr-2 text-green-500" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Impact */}
        <Section title="Impact Attendu" icon={ChartBar}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.market.map((impact, idx) => (
                <Metric
                  key={idx}
                  label={impact.metric}
                  value={impact.target}
                  trend={`Objectif ${impact.timeline}`}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {impacts.stakeholders.map((stakeholder, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">{stakeholder.type}</h3>
                  <ul className="space-y-2">
                    {stakeholder.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Défis */}
        <Section title="Défis et Risques" icon={AlertTriangle}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {challenges.map((category, idx) => (
              <div key={idx}>
                <h3 className="font-medium mb-4">{category.category}</h3>
                <div className="space-y-4">
                  {category.items.map((item, i) => (
                    <div key={i} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{item.challenge}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.impact === 'Élevé'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          Impact: {item.impact}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Mitigation: {item.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default KenyaDigitalFinance;